import { differenceInHours } from 'date-fns';
import { log } from 'evlog/client';
import {
  Ban,
  CalendarCheck,
  CheckCircle2,
  Clock,
  FileText,
  Home,
  Info,
  MapPin,
  Phone,
  Trash2,
  User,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';

import type { GetHomeCareReservationById } from '@virtual-care/client';
import {
  displayName,
  formatDate,
  formatReservationDate,
  formatReservationTime,
} from '@virtual-care/isomorphic';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@virtual-care/shadcn';
import {
  Loading,
  ReservationStatus,
  TheButton,
  useAction,
  useAppointmentStarted,
  useData,
} from '@virtual-care/ui';

import { Header, ProfileCard } from '../../../components/header/index.ts';

type TimelineEvent = GetHomeCareReservationById['timeline'][number];

const timelineIconConfig: Record<
  TimelineEvent['type'],
  { icon: typeof Clock; color: string }
> = {
  CREATED: { icon: Clock, color: 'text-blue-500' },
  CONFIRMED: { icon: CheckCircle2, color: 'text-green-500' },
  FINISHED: { icon: CalendarCheck, color: 'text-green-500' },
  CANCELED: { icon: Ban, color: 'text-red-500' },
};

const CANCEL_WINDOW_HOURS = 6;

const statusIconConfig: Record<
  GetHomeCareReservationById['status'],
  { icon: typeof Clock; className: string }
> = {
  WAITING_CONFIRMATION: {
    icon: Clock,
    className: 'border-yellow-300 bg-yellow-50 text-yellow-700',
  },
  CONFIRMED: {
    icon: CheckCircle2,
    className: 'border-green-300 bg-green-50 text-green-700',
  },
  FINISHED: {
    icon: CalendarCheck,
    className: 'border-gray-300 bg-gray-50 text-gray-700',
  },
  CANCELED: {
    icon: Ban,
    className: 'border-red-300 bg-red-50 text-red-700',
  },
};

export default function HomecareReservation() {
  const params = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isFinishDialogOpen, setIsFinishDialogOpen] = useState(false);

  const {
    data: reservation,
    isLoading,
    refetch,
  } = useData('GET /patients/me/homecare-reservations/{id}', {
    id: params.id!,
  });

  const cancelReservation = useAction(
    'POST /homecare/patient/reservations/{id}/cancel',
    {
      invalidate: [
        'GET /patients/me/homecare-reservations',
        'GET /patients/me/homecare-reservations/{id}',
      ],
    },
  );

  const finishReservation = useAction(
    'POST /homecare/patient/reservations/{id}/finish',
    {
      invalidate: [
        'GET /patients/me/homecare-reservations',
        'GET /patients/me/homecare-reservations/{id}',
      ],
    },
  );

  const appointmentStarted = useAppointmentStarted(reservation);

  const handleFinish = async () => {
    log.info({ action: 'finish_homecare', reservationId: params.id });
    try {
      // `invalidate` above already refetches the list and this detail query.
      await finishReservation.mutateAsync({ id: params.id! });
      toast.success(t('homecare.reservationDetail.finishSuccess'));
    } catch {
      // e.g. the vendor finished first (invalid-status); the refetch below
      // syncs the page to whatever the reservation actually is now.
      toast.error(t('homecare.reservationDetail.finishError'));
      refetch();
    } finally {
      setIsFinishDialogOpen(false);
    }
  };

  const handleCancel = async () => {
    log.info({ action: 'cancel_homecare', reservationId: params.id });
    if (!reservation?.scheduledDate) {
      toast.error(t('homecare.reservationDetail.cancelErrors.missingDate'));
      return;
    }

    const hoursDifference = differenceInHours(
      new Date(reservation.scheduledDate),
      new Date(),
    );

    if (hoursDifference <= CANCEL_WINDOW_HOURS) {
      toast.error(
        t('homecare.reservationDetail.cancelErrors.tooLate', {
          hours: CANCEL_WINDOW_HOURS,
        }),
      );
      setIsCancelDialogOpen(false);
      return;
    }

    try {
      await cancelReservation.mutateAsync({ id: params.id! });
      toast.success(t('homecare.reservationDetail.cancelSuccess'));
    } catch {
      toast.error(t('homecare.reservationDetail.cancelError'));
      refetch();
    } finally {
      setIsCancelDialogOpen(false);
    }
  };

  if (isLoading) return <Loading />;

  if (!reservation) {
    return <div>{t('homecare.reservationDetail.notFound')}</div>;
  }

  const statusInfo = statusIconConfig[reservation.status];
  const StatusIcon = statusInfo.icon;
  const statusLabel = t(
    `homecare.reservationDetail.statuses.${reservation.status}`,
  );

  const notSpecified = t('common.notSpecified');
  const vendorName =
    reservation.vendor.vendorType === 'INDIVIDUAL'
      ? `${reservation.vendor.individualProfile?.firstName ?? ''} ${reservation.vendor.individualProfile?.lastName ?? ''}`.trim() ||
        notSpecified
      : (reservation.vendor.clinicProfile?.clinicName ?? notSpecified);

  const vendorCity =
    reservation.vendor.vendorType === 'INDIVIDUAL'
      ? reservation.vendor.individualProfile?.cityName
      : reservation.vendor.clinicProfile?.cityName;

  const formattedDate = formatReservationDate(reservation);
  const formattedTime = formatReservationTime(reservation);
  const price = (Number(reservation.vendorService.price) / 100).toFixed(2);

  const canCancel =
    reservation.status === 'WAITING_CONFIRMATION' ||
    reservation.status === 'CONFIRMED';

  const canFinish = reservation.status === 'CONFIRMED';

  return (
    <>
      <Header
        title={
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="cursor-pointer"
            >
              <span className="sr-only">
                {t('homecare.reservationDetail.backAria')}
              </span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m9 18 6-6-6-6" />
              </svg>
            </Button>
            <div className="flex items-center gap-2">
              <Home className="bg-chart-3 h-8 w-8 rounded-full p-1.5 text-white" />
              <span className="text-lg font-bold">
                {t('homecare.reservationDetail.headerTitle', {
                  counter: reservation.counter,
                })}
              </span>
              <ReservationStatus.Root status={reservation.status}>
                <ReservationStatus.Label render={(meta) => t(meta.key)} />
              </ReservationStatus.Root>
            </div>
          </div>
        }
      >
        <div className="hidden md:block">
          <ProfileCard />
        </div>
      </Header>

      <div className="min-h-screen px-4 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="col-span-1 flex flex-col gap-6 lg:col-span-2">
            <div
              className={`flex items-center gap-3 rounded-lg border p-4 ${statusInfo.className}`}
            >
              <StatusIcon className="h-6 w-6" />
              <span className="font-medium">{statusLabel}</span>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-right">
                  <Info className="h-5 w-5" />
                  {t('homecare.reservationDetail.sectionTitle')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <span className="text-muted-foreground text-sm">
                      {t('homecare.reservationDetail.service')}
                    </span>
                    <p className="font-semibold">
                      {reservation.vendorService.service.name}
                    </p>
                  </div>
                  <div>
                    <span className="text-muted-foreground text-sm">
                      {t('homecare.reservationDetail.date')}
                    </span>
                    <p className="font-semibold">{formattedDate}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground text-sm">
                      {t('homecare.reservationDetail.time')}
                    </span>
                    <p className="font-semibold">{formattedTime}</p>
                  </div>
                  <div>
                    <span className="text-muted-foreground text-sm">
                      {t('homecare.reservationDetail.price')}
                    </span>
                    <p className="font-semibold">
                      {price} {t('homecare.serviceListing.priceCurrency')}
                    </p>
                  </div>
                </div>

                {reservation.description && (
                  <div className="border-t pt-4">
                    <span className="text-muted-foreground text-sm">
                      {t('homecare.reservationDetail.descriptionLabel')}
                    </span>
                    <p className="mt-1 whitespace-pre-wrap">
                      {reservation.description}
                    </p>
                  </div>
                )}

                {(canFinish || canCancel) && (
                  <div className="flex flex-col gap-2 border-t pt-4">
                    <div className="flex flex-wrap justify-end gap-2">
                      {canFinish && (
                        <Button
                          variant="default"
                          className="cursor-pointer gap-2 bg-green-600 text-sm font-bold text-white hover:bg-green-700"
                          disabled={
                            finishReservation.isPending || !appointmentStarted
                          }
                          onClick={() => setIsFinishDialogOpen(true)}
                        >
                          {t('homecare.reservationDetail.finishButton')}{' '}
                          <CalendarCheck className="h-5 w-5" />
                        </Button>
                      )}
                      {canCancel && (
                        <Button
                          variant="outline"
                          className="border-destructive text-destructive hover:text-destructive cursor-pointer gap-2 text-sm font-bold"
                          disabled={cancelReservation.isPending}
                          onClick={() => setIsCancelDialogOpen(true)}
                        >
                          {t('homecare.reservationDetail.cancelButton')}{' '}
                          <Trash2 className="h-5 w-5" />
                        </Button>
                      )}
                    </div>
                    {canFinish && !appointmentStarted && (
                      <p className="text-muted-foreground text-end text-xs">
                        {t('homecare.reservationDetail.finishNotStartedYet')}
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {reservation.files.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-right">
                    <FileText className="h-5 w-5" />
                    {t('homecare.reservationDetail.attachmentsTitle')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-3 md:grid-cols-2">
                    {reservation.files.map((file) => (
                      <a
                        key={file.id}
                        href={file.file.path}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 rounded-lg border p-3 transition-colors hover:bg-gray-50"
                      >
                        <FileText className="h-5 w-5 text-gray-500" />
                        <span className="truncate text-sm">
                          {file.file.name}
                        </span>
                      </a>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-right">
                  <Clock className="h-5 w-5" />
                  {t('homecare.reservationDetail.timelineTitle')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="relative space-y-4">
                  {reservation.timeline.map((event, index) => {
                    const config = timelineIconConfig[event.type];
                    const EventIcon = config.icon;
                    return (
                      <div key={event.id} className="flex gap-4">
                        <div className="relative flex flex-col items-center">
                          <div
                            className={`flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 ${config.color}`}
                          >
                            <EventIcon className="h-5 w-5" />
                          </div>
                          {index < reservation.timeline.length - 1 && (
                            <div className="absolute top-10 h-full w-0.5 bg-gray-200" />
                          )}
                        </div>
                        <div className="flex-1 pb-6">
                          <p className="font-semibold">
                            {t(
                              `homecare.reservationDetail.timeline.${event.type}`,
                            )}
                          </p>
                          <p className="text-muted-foreground text-sm">
                            {formatDate(event.createdAt)}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="sticky top-4 col-span-1 flex flex-col gap-6 self-start">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-right">
                  <User className="h-5 w-5" />
                  {t('homecare.reservationDetail.providerTitle')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="bg-chart-3 flex h-12 w-12 items-center justify-center rounded-full">
                    {reservation.vendor.vendorType === 'INDIVIDUAL' ? (
                      <User className="h-6 w-6 text-white" />
                    ) : (
                      <Home className="h-6 w-6 text-white" />
                    )}
                  </div>
                  <div>
                    <p className="font-semibold">{vendorName}</p>
                    <Badge variant="secondary">
                      {reservation.vendor.vendorType === 'INDIVIDUAL'
                        ? t('homecare.reservationDetail.vendorTypeIndividual')
                        : t('homecare.reservationDetail.vendorTypeClinic')}
                    </Badge>
                  </div>
                </div>

                <div className="space-y-2 border-t pt-4">
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="text-muted-foreground h-4 w-4" />
                    <span>{vendorCity}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="text-muted-foreground h-4 w-4" />
                    <span dir="ltr">{reservation.vendor.phoneNumber}</span>
                  </div>
                </div>

                {reservation.vendor.bio && (
                  <div className="border-t pt-4">
                    <span className="text-muted-foreground text-sm">
                      {t('homecare.reservationDetail.providerBio')}
                    </span>
                    <p className="mt-1 text-sm">{reservation.vendor.bio}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-right">
                  <User className="h-5 w-5" />
                  {t('homecare.reservationDetail.patientTitle')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground text-sm">
                    {t('homecare.reservationDetail.patientName')}
                  </span>
                  <span className="font-semibold">
                    {displayName(reservation.patient)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground text-sm">
                    {t('homecare.reservationDetail.patientPhone')}
                  </span>
                  <span dir="ltr">{reservation.patient.phoneNumber}</span>
                </div>
                {reservation.patient.email && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground text-sm">
                      {t('homecare.reservationDetail.patientEmail')}
                    </span>
                    <span>{reservation.patient.email}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <Dialog open={isCancelDialogOpen} onOpenChange={setIsCancelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('homecare.reservationDetail.cancelDialog.title')}
            </DialogTitle>
            <DialogDescription>
              {t('homecare.reservationDetail.cancelDialog.description', {
                counter: reservation.counter,
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setIsCancelDialogOpen(false)}
              className="cursor-pointer"
            >
              {t('homecare.reservationDetail.cancelDialog.cancel')}
            </Button>
            <TheButton
              variant="destructive"
              onClick={handleCancel}
              loading={cancelReservation.isPending}
              className="cursor-pointer"
            >
              {t('homecare.reservationDetail.cancelDialog.confirm')}
            </TheButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isFinishDialogOpen} onOpenChange={setIsFinishDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('homecare.reservationDetail.finishDialog.title')}
            </DialogTitle>
            <DialogDescription>
              {t('homecare.reservationDetail.finishDialog.description', {
                counter: reservation.counter,
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setIsFinishDialogOpen(false)}
              className="cursor-pointer"
            >
              {t('homecare.reservationDetail.finishDialog.cancel')}
            </Button>
            <TheButton
              variant="default"
              onClick={handleFinish}
              loading={finishReservation.isPending}
              className="cursor-pointer bg-green-600 hover:bg-green-700"
            >
              {t('homecare.reservationDetail.finishDialog.confirm')}
            </TheButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
